// Schema.org structured data helpers for Bush's Beans
// Each builder returns a plain object; buildPageSchema assembles the full @graph.

const SITE_URL = 'https://www.bushbeans.com';
const FS_SITE_URL = 'https://www.bushbeansfoodservice.com';

function getSiteConfig(siteName, siteDomain) {
  const isFs = siteName === 'bushs-beans-foodservices';
  const siteUrl = (siteDomain || (isFs ? FS_SITE_URL : SITE_URL)).replace(/\/$/, '');
  return {
    siteUrl,
    orgId: `${siteUrl}/#organization`,
    websiteId: `${siteUrl}/#website`,
    brandId: `${siteUrl}/#brand`,
    isFs,
  };
}

const PAGE_TYPES = {
  HOME: 'home',
  RECIPE_DETAIL: 'recipe_detail',
  RECIPE_LISTING: 'recipe_listing',
  PRODUCT_DETAIL: 'product_detail',
  PRODUCT_CATEGORY: 'product_category',
  ARTICLE: 'article',
  VISITOR_CENTER: 'visitor_center',
  DEFAULT: 'default',
};

// ─── Page type detection ──────────────────────────────────────────────────────

export function detectPageType(metadataUrl = '') {
  const path = metadataUrl.toLowerCase().replace(/\/$/, '');

  // Homepage: /en_US or /en_CA (locale only, no sub-path)
  if (/^\/[a-z]{2}_[a-z]{2}$/.test(path) || path === '') return PAGE_TYPES.HOME;

  if (path.includes('/about-us/visitor-center')) return PAGE_TYPES.VISITOR_CENTER;

  // Recipe: detail pages have a slug segment after /bean-recipes/
  if (path.includes('/bean-recipes/')) return PAGE_TYPES.RECIPE_DETAIL;
  if (path.includes('/bean-recipes')) return PAGE_TYPES.RECIPE_LISTING;

  // Bean Hub articles
  if (path.includes('/bean-hub/article/') || (path.includes('/bean-hub/') && !path.endsWith('/bean-hub'))) {
    return PAGE_TYPES.ARTICLE;
  }

  // Products: /products/category/... or /products/.../... vs /products/[slug]
  if (path.includes('/products/category/')) return PAGE_TYPES.PRODUCT_CATEGORY;
  if (path.includes('/products/')) return PAGE_TYPES.PRODUCT_DETAIL;

  return PAGE_TYPES.DEFAULT;
}

// ─── URL utilities ────────────────────────────────────────────────────────────

function buildFullUrl(siteUrl, metadataUrl) {
  const base = siteUrl.replace(/\/$/, '');
  return metadataUrl ? `${base}${metadataUrl}` : base;
}

function slugToTitle(slug = '') {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

const SEGMENT_LABELS = {
  'bean-recipes': 'Recipes',
  'products': 'Products',
  'bean-hub': 'Bean Hub',
  'about-us': 'About Us',
  'category': null, // skip 'category' segment in breadcrumb
  'article': null,  // skip 'article' segment in breadcrumb
};

export function buildBreadcrumbSchema(siteUrl, metadataUrl, pageTitle) {
  const base = siteUrl.replace(/\/$/, '');
  const segments = metadataUrl
    .replace(/^\/[a-z]{2}_[a-z]{2}/, '') // strip locale prefix
    .split('/')
    .filter(Boolean);

  if (segments.length === 0) return null;

  const items = [{ name: "Home", url: `${base}/en_US/` }];
  let cumulativePath = '/en_US';

  segments.forEach((seg, idx) => {
    const label = SEGMENT_LABELS[seg];
    if (label === null) return; // skip utility segments like 'article', 'category'

    cumulativePath += `/${seg}`;
    const isLast = idx === segments.length - 1 || segments.slice(idx + 1).every(s => SEGMENT_LABELS[s] === null);
    const name = isLast && pageTitle ? pageTitle : (label || slugToTitle(seg));

    items.push({ name, url: `${base}${cumulativePath}` });
  });

  if (items.length < 2) return null;

  return {
    '@type': 'BreadcrumbList',
    '@id': `${base}${metadataUrl}#breadcrumb`,
    'itemListElement': items.map((item, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'name': item.name,
      'item': item.url,
    })),
  };
}

// ─── Base schemas (every page) ────────────────────────────────────────────────

function buildOrganizationSchema({ siteUrl, orgId, brandId, isFs }) {
  const logoUrl = isFs
    ? `${siteUrl}/-/jssmedia/BushsBeans/MegaMenu/Bush_logo_2020_rgb-(1).png` // update if FS gets its own logo
    : `${siteUrl}/-/jssmedia/BushsBeans/MegaMenu/Bush_logo_2020_rgb-(1).png`;

  return {
    '@type': 'Organization',
    '@id': orgId,
    name: 'Bush Brothers & Company',
    alternateName: ["Bush's Beans", "BUSH'S Beans", "Bush's Best"],
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      '@id': `${siteUrl}/#logo`,
      url: logoUrl,
    },
    sameAs: [
      'https://www.facebook.com/bushsbeans',
      'https://www.instagram.com/bushsbeans',
      'https://www.youtube.com/user/BushsBeans',
      'https://www.pinterest.com/bushsbeans',
    ],
    brand: {
      '@type': 'Brand',
      '@id': brandId,
      name: "Bush's Beans",
      url: siteUrl,
    },
  };
}

function buildWebSiteSchema({ siteUrl, orgId, websiteId, isFs }, siteName) {
  const defaultName = isFs ? "BUSH'S Beans Foodservice" : "BUSH'S Beans";
  return {
    '@type': 'WebSite',
    '@id': websiteId,
    'url': siteUrl,
    'name': siteName || defaultName,
    'publisher': { '@id': orgId },
    'inLanguage': 'en-US',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': `${siteUrl}/en_US/search?keyword={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

function buildWebPageSchema({ siteUrl, websiteId }, { metadataUrl, canonicalUrl, pageTitle, metadataTitle, metadataDescription, metadataImage }) {
  const pageUrl = canonicalUrl || buildFullUrl(siteUrl, metadataUrl);
  return {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    'url': pageUrl,
    'name': metadataTitle || pageTitle || "BUSH'S Beans",
    ...(metadataDescription ? { 'description': metadataDescription } : {}),
    'isPartOf': { '@id': websiteId },
    ...(metadataImage ? {
      'primaryImageOfPage': {
        '@type': 'ImageObject',
        'url': metadataImage.startsWith('http') ? metadataImage : `${siteUrl}${metadataImage}`,
      },
    } : {}),
    'inLanguage': 'en-US',
  };
}

// ─── Page-type specific schemas ───────────────────────────────────────────────

function buildSiteNavigationSchema({ siteUrl, isFs }) {
  const base = siteUrl.replace(/\/$/, '');
  if (isFs) {
    return {
      '@type': 'SiteNavigationElement',
      '@id': `${base}/#site-navigation`,
      'name': ['Insights', 'Recipes', 'Products', 'Contact a Rep'],
      'url': [
        `${base}/en_US/insights`,
        `${base}/en_US/recipes`,
        `${base}/en_US/bulk-products`,
        `${base}/contact`,
      ],
    };
  }
  return {
    '@type': 'SiteNavigationElement',
    '@id': `${base}/#site-navigation`,
    'name': ['Recipes', 'Products', 'Bean Hub', 'About Us', 'Find In-Store', 'Shop Bean Swag'],
    'url': [
      `${base}/en_US/bean-recipes`,
      `${base}/en_US/products`,
      `${base}/en_US/bean-hub`,
      `${base}/en_US/about-us`,
      `${base}/en_US/find-in-store`,
      'https://shop.bushbeans.com/',
    ],
  };
}

function buildArticleSchema({ siteUrl, orgId }, { metadataUrl, canonicalUrl, pageTitle, metadataTitle, metadataDescription, metadataImage, datePublished, dateModified }) {
  const pageUrl = canonicalUrl || buildFullUrl(siteUrl, metadataUrl);
  const imgSrc = metadataImage
    ? (metadataImage.startsWith('http') ? metadataImage : `${siteUrl}${metadataImage}`)
    : undefined;

  return {
    '@type': 'Article',
    '@id': `${pageUrl}#article`,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
    },
    'headline': metadataTitle || pageTitle || '',
    ...(metadataDescription ? { 'description': metadataDescription } : {}),
    ...(imgSrc ? { 'image': [imgSrc] } : {}),
    ...(datePublished ? { 'datePublished': datePublished } : {}),
    ...(dateModified ? { 'dateModified': dateModified } : {}),
    'author': { '@type': 'Organization', '@id': orgId, 'name': "Bush's Beans" },
    'publisher': { '@type': 'Organization', '@id': orgId },
  };
}

function buildProductSchema({ siteUrl, orgId, brandId }, { metadataUrl, canonicalUrl, pageTitle, metadataTitle, metadataDescription, metadataImage }) {
  const pageUrl = canonicalUrl || buildFullUrl(siteUrl, metadataUrl);
  const imgSrc = metadataImage
    ? (metadataImage.startsWith('http') ? metadataImage : `${siteUrl}${metadataImage}`)
    : undefined;

  return {
    '@type': 'Product',
    '@id': `${pageUrl}#product`,
    'name': metadataTitle || pageTitle || '',
    'url': pageUrl,
    ...(imgSrc ? { 'image': [imgSrc] } : {}),
    ...(metadataDescription ? { 'description': metadataDescription } : {}),
    'brand': { '@type': 'Brand', '@id': brandId, 'name': "Bush's Beans" },
    'manufacturer': { '@type': 'Organization', '@id': orgId, 'name': 'Bush Brothers & Company' },
    'category': 'Canned Beans',
  };
}

function buildCollectionPageSchema({ siteUrl, websiteId }, { metadataUrl, canonicalUrl, pageTitle, metadataTitle, metadataDescription }) {
  const pageUrl = canonicalUrl || buildFullUrl(siteUrl, metadataUrl);
  return {
    '@type': 'CollectionPage',
    '@id': `${pageUrl}#webpage`,
    'url': pageUrl,
    'name': metadataTitle || pageTitle || '',
    ...(metadataDescription ? { 'description': metadataDescription } : {}),
    'isPartOf': { '@id': websiteId },
  };
}

function buildTouristAttractionSchema({ siteUrl, orgId }) {
  const pageUrl = `${siteUrl}/en_US/about-us/visitor-center`;
  return {
    '@type': 'TouristAttraction',
    '@id': `${pageUrl}#visitor-center`,
    'name': "Bush's Beans Visitor Center",
    'url': pageUrl,
    'description': "The Bush's Beans Visitor Center in Chestnut Hill, Tennessee shares the history of Bush's Beans, the Bush family and the brand's 110+ year bean story.",
    'isPartOf': { '@id': orgId },
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Chestnut Hill',
      'addressRegion': 'TN',
      'addressCountry': 'US',
    },
  };
}

// ─── Main assembler ───────────────────────────────────────────────────────────

/**
 * Builds the full schema @graph for the current page.
 *
 * @param {object} sitecoreContext - the full sitecoreContext object from Layout
 * @returns {string|null} - JSON string ready for <script type="application/ld+json">, or null
 */
export function buildPageSchema(sitecoreContext) {
  if (!sitecoreContext) return null;

  const { globalContent = {}, site = {} } = sitecoreContext;
  const { metadata = {}, siteName = '' } = globalContent;
  const {
    pageTitle = '',
    metadataTitle = '',
    metadataDescription = '',
    metadataImage = '',
    metadataUrl = '',
    canonicalUrl = '',
    siteDomain = '',
    datePublished = '',
    dateModified = '',
  } = metadata;

  const siteConfig = getSiteConfig(site?.name, siteDomain);
  const pageType = detectPageType(metadataUrl);
  const metaProps = { metadataUrl, canonicalUrl, pageTitle, metadataTitle, metadataDescription, metadataImage };

  const graph = [
    buildOrganizationSchema(siteConfig),
    buildWebSiteSchema(siteConfig, siteName),
  ];

  // WebPage is replaced by CollectionPage on listing pages (which extends WebPage)
  const isCollectionPage = pageType === PAGE_TYPES.RECIPE_LISTING || pageType === PAGE_TYPES.PRODUCT_CATEGORY;

  if (isCollectionPage) {
    graph.push(buildCollectionPageSchema(siteConfig, metaProps));
  } else {
    graph.push(buildWebPageSchema(siteConfig, metaProps));
  }

  // Page-type additions
  switch (pageType) {
    case PAGE_TYPES.HOME:
      graph.push(buildSiteNavigationSchema(siteConfig));
      break;

    case PAGE_TYPES.ARTICLE:
      graph.push(buildArticleSchema(siteConfig, { ...metaProps, datePublished, dateModified }));
      break;

    case PAGE_TYPES.PRODUCT_DETAIL:
      graph.push(buildProductSchema(siteConfig, metaProps));
      break;

    case PAGE_TYPES.VISITOR_CENTER:
      graph.push(buildTouristAttractionSchema(siteConfig));
      break;

    default:
      break;
  }

  // BreadcrumbList for all non-homepage pages
  if (pageType !== PAGE_TYPES.HOME && metadataUrl) {
    const breadcrumb = buildBreadcrumbSchema(siteConfig.siteUrl, metadataUrl, pageTitle || metadataTitle);
    if (breadcrumb) graph.push(breadcrumb);
  }

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}